import React, { useState, useEffect } from 'react';
import { User, ArrowRight, Star, Menu, X, UserCircle, Home, LogOut } from 'lucide-react';
import './Membership.css';
import logo from '../components/assets/logo.png';
import { useNavigate } from "react-router-dom";
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Local Storage Helper
const LocalStorageHelper = {
  saveLoginData: (userId, email, name, userType = 'user') => {
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('userId', userId);
    localStorage.setItem('userEmail', email);
    localStorage.setItem('userName', name);
    localStorage.setItem('userType', userType);
  },

  isLoggedIn: () => {
    return localStorage.getItem('isLoggedIn') === 'true';
  },

  getLoginData: () => {
    return {
      userId: localStorage.getItem('userId'),
      email: localStorage.getItem('userEmail'),
      name: localStorage.getItem('userName'),
      userType: localStorage.getItem('userType'),
    };
  },

  clearLoginData: () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('userType');
  }
};

const Membership = () => {
  const navigate = useNavigate();
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // NEW: Authentication state
  const [currentUser, setCurrentUser] = useState(null);
  const [photographerData, setPhotographerData] = useState(null);
  const [photographerName, setPhotographerName] = useState('Photographer');
  const [profilePhoto, setProfilePhoto] = useState(null);

  // NEW: Authentication check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        await loadPhotographerData(user.uid);
      } else {
        navigate('/Login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // NEW: Load photographer data to get name and profile photo
  const loadPhotographerData = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'photographers', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setPhotographerData(userData);
        setPhotographerName(userData.name || userData.displayName || 'Photographer');
        setProfilePhoto(userData.profileImage || null);
      } else {
        // Fallback to auth user data
        if (auth.currentUser) {
          setPhotographerName(auth.currentUser.displayName || 'Photographer');
        }
      }
    } catch (error) {
      console.error('Error loading photographer data:', error);
      // Fallback to auth user data
      if (auth.currentUser) {
        setPhotographerName(auth.currentUser.displayName || 'Photographer');
      }
    }
  };

  // NEW: Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      LocalStorageHelper.clearLoginData();
      navigate('/Login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // NEW: Handle home navigation
  const handleHomeNavigation = () => {
    navigate('/LandingPage');
    closeSidebar();
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile && !sidebarVisible) {
        setSidebarVisible(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarVisible]);

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarVisible(false);
    }
  };

  const membershipPlans = [
    {
      id: 1,
      title: 'Starter',
      price: '25,000',
      period: '/ yearly + taxes',
      features: [
        'Estimated leads yearly: 83-138',
        'Get more visibility than free plan',
        'Basic profile setup',
        'Email support',
        'Standard analytics'
      ],
      buttonText: 'Get Started',
      isPopular: false
    },
    {
      id: 2,
      title: 'Premium',
      price: '50,000',
      period: '/ yearly + taxes',
      features: [
        'Estimated leads yearly: 330-550',
        'Get more than 4x visibility of Starter plan',
        'Get more than 2x visibility of Advanced',
        'Profile management support',
        'Priority customer support',
        'Advanced analytics & insights'
      ],
      buttonText: 'Choose Premium',
      isPopular: false
    },
    {
      id: 3,
      title: 'Advanced',
      price: '35,000',
      period: '/ yearly + taxes',
      features: [
        'Estimated leads yearly: 165-275',
        'Get more than 2x visibility of Starter Plan',
        'Profile management support',
        'Phone & email support',
        'Enhanced analytics'
      ],
      buttonText: 'Upgrade Now',
      isPopular: false
    }
  ];

  const handleProceedPayment = (planTitle) => {
    console.log(`Proceeding with ${planTitle} plan payment`);
    // Add your payment logic here
  };

  return (
    <div className="membership-container">
      {/* Toggle Button */}
      <button 
        className={`membership-toggle-btn ${!sidebarVisible ? 'active' : ''}`}
        onClick={toggleSidebar}
      >
        <div className="membership-toggle-icon">
          {sidebarVisible ? <Menu size={20} /> : <X size={20} />}
        </div>
      </button>

      {/* Overlay for mobile */}
      {isMobile && sidebarVisible && (
        <div className="membership-overlay active" onClick={closeSidebar}></div>
      )}

      {/* Sidebar */}
      <aside className={`membership-sidebar ${
        isMobile 
          ? (sidebarVisible ? 'show' : '') 
          : (sidebarVisible ? '' : 'hidden')
      }`}>
        <img src={logo} alt="LitStudio" className="membership-logo" />
        
        <nav>
          <ul className="membership-nav">
            <li className="membership-nav-item" onClick={() => {
              navigate("/Dashboard");
              closeSidebar();
            }}>
              📊 Dashboard
            </li>
            <li className="membership-nav-item" onClick={() => {
              navigate("/Upload");
              closeSidebar();
            }}>
              📤 Upload
            </li>
            <li className="membership-nav-item membership-nav-item--active">
              👑 Membership Plans
            </li>
            <li className="membership-nav-item" onClick={() => {
              navigate("/PhotographProfile");
              closeSidebar();
            }}>
              <UserCircle size={16} style={{ marginRight: '8px' }} />
              Profile
            </li>
          </ul>
        </nav>
        
        {/* UPDATED: User Profile Section with Photographer Name and Navigation Buttons */}
        <div className="membership-user-section">
          <div className="membership-user-info">
            <div className="membership-user-avatar">
              {profilePhoto ? (
                <img 
                  src={profilePhoto} 
                  alt="Profile" 
                  className="membership-user-avatar-image"
                />
              ) : (
                <UserCircle size={24} />
              )}
            </div>
            <div className="membership-welcome-text">
              <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                Welcome back 👋
              </div>
              <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '2px' }}>
                {photographerName}
              </div>
            </div>
          </div>
          
          {/* NEW: Navigation Buttons */}
          <div className="membership-user-actions">
            <button 
              className="membership-user-action-btn membership-home-btn"
              onClick={handleHomeNavigation}
              title="Go to Home"
            >
              <Home size={16} />
              <span>Home</span>
            </button>
            <button 
              className="membership-user-action-btn membership-logout-btn"
              onClick={handleLogout}
              title="Logout"
            >
              <LogOut size={16} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>
      
      {/* Main Content - Rest of your existing JSX remains exactly the same */}
      <main className={`membership-main-content ${
        !sidebarVisible && !isMobile ? 'expanded' : ''
      }`}>
        <h1 className="membership-page-title">Membership Plans</h1>
        
        {/* Pricing Cards */}
        <div className="membership-pricing-container">
          {membershipPlans.map((plan) => (
            <div 
              key={plan.id}
              className="membership-pricing-card"
            >
              <h2 className="membership-plan-title">{plan.title}</h2>
              <div className="membership-price">
                <span className="membership-currency">₹</span>
                <span className="membership-amount">{plan.price}</span>
                <span className="membership-period">{plan.period}</span>
              </div>
              
              <ul className="membership-features-list">
                {plan.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
              
              <button 
                className="membership-proceed-button"
                onClick={() => handleProceedPayment(plan.title)}
              >
                💳 {plan.buttonText} <ArrowRight size={16} />
              </button>
            </div>
          ))}
        </div>
        
        {/* Stats Section */}
        <div style={{ 
          marginBottom: '2rem', 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1rem' 
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            padding: '1.25rem',
            borderRadius: '16px',
            border: '1px solid rgba(230, 230, 230, 0.6)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ff4d4d', marginBottom: '0.5rem' }}>
              🎯 1000+
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>Happy Clients</div>
          </div>
          
          <div style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            padding: '1.25rem',
            borderRadius: '16px',
            border: '1px solid rgba(230, 230, 230, 0.6)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ff4d4d', marginBottom: '0.5rem' }}>
              ⭐ 4.8/5
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>Average Rating</div>
          </div>
          
          <div style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            padding: '1.25rem',
            borderRadius: '16px',
            border: '1px solid rgba(230, 230, 230, 0.6)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ff4d4d', marginBottom: '0.5rem' }}>
              🚀 99%
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>Success Rate</div>
          </div>
        </div>
        
        {/* Terms and Conditions */}
        <div className="membership-terms-container">
          <h2 className="membership-terms-title">Terms and Conditions</h2>
          
          <h3 className="membership-agreement-title">📋 Your Agreement</h3>
          
          <ol className="membership-terms-list">
            <li>
              <p><strong>Plagiarism Policy:</strong> Any instance of plagiarism (not your original work) found on the profile will result in immediate termination of the subscription plan for the next 3 months. Repeated instances will result in profile blacklisting.</p>
            </li>
            <li>
              <p><strong>Payment Policy:</strong> All payments made to Lit Studio are non-refundable under any circumstances.</p>
            </li>
            <li>
              <p><strong>Setup Fee:</strong> Setup fee is charged on every subscription and is completely non-refundable and non-adjustable against any future subscriptions.</p>
            </li>
            <li>
              <p><strong>Lead Estimates:</strong> Any lead count quoted is just an estimate based on package MRP, past performance, and market conditions. These do not guarantee specific lead volumes.</p>
            </li>
            <li>
              <p><strong>Profile Optimization:</strong> To maximize your subscription benefits, regularly update your profile with fresh content, respond to client reviews promptly, and maintain competitive pricing.</p>
            </li>
            <li>
              <p><strong>Location Packages:</strong> A base location package is mandatory for additional city packages. If base location expires, all other packages will also expire.</p>
            </li>
          </ol>
          
          <div className="membership-terms-checkbox">
            <input 
              type="checkbox" 
              id="membership-accept-terms" 
              checked={termsAccepted}
              onChange={() => setTermsAccepted(!termsAccepted)}
            />
            <label htmlFor="membership-accept-terms">
               I confirm that I have read and accept the terms and conditions and privacy policy.
            </label>
          </div>
          
          <div className="membership-terms-actions">
            <button className="membership-cancel-button" onClick={() => {
              console.log('Terms cancelled');
              setTermsAccepted(false);
            }}>
               Cancel
            </button>
            <button 
              className={`membership-accept-button ${termsAccepted ? '' : 'disabled'}`}
              disabled={!termsAccepted}
              onClick={() => {
                if (termsAccepted) {
                  console.log('Terms accepted');
                  // Add your acceptance logic here
                }
              }}
            >
               Accept & Continue
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Membership;
